import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, map, Observable } from 'rxjs';
import { ActionSuggestion } from '@panaya/ai-chat/shared';
import { AiChatFinishTypingService } from '../ai-chat-finish-typing.service';

/**
 * Service that provides action suggestions extracted from prompt responses.
 * This supports both old server format (top-level commands) and new server format (tool_complete wrapper).
 *
 * The interceptor automatically calls `notifyActionsSuggestions()` when processing prompt responses.
 * Suggestions are gated and only emitted after typing completes (signaled by AiChatFinishTypingService).
 * No configuration or providers are needed - this works automatically for both server formats.
 */
@Injectable({ providedIn: 'root' })
export class AiChatActionsSuggestionsProviderService {
    private readonly finishTypingService = inject(AiChatFinishTypingService);
    private readonly actionsSuggestionsSubject$ = new BehaviorSubject<ActionSuggestion[] | undefined>(undefined);

    /**
     * Observable stream of processed action suggestions extracted from prompt responses.
     * Only emits after typing has completed (AiChatFinishTypingService.finishTyping$ has fired).
     * Uses ReplaySubject semantics in AiChatFinishTypingService so late subscribers never miss the signal.
     */
    public readonly actionsSuggestions$: Observable<ActionSuggestion[] | undefined> = combineLatest([
        this.actionsSuggestionsSubject$.asObservable(),
        this.finishTypingService.finishTyping$
    ]).pipe(
        map(([suggestions, _]) => suggestions)
    );

    /**
     * Stores action suggestions internally to be emitted once typing completes.
     * Called internally by the interceptor when processing prompt responses.
     */
    public notifyActionsSuggestions(suggestions: ActionSuggestion[] | undefined): void {
        this.actionsSuggestionsSubject$.next(suggestions);
    }
}
